/**
 * @file ReviewFlagsPanel.js
 * @version 1.4.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-19
 * @modified 2026-10-20
 *
 * 1.4.0 (2026-10-20): `hide` — flag groups left out (an interim statement
 * run hides "paid just after the period end": its "Since" section lists
 * those payments).
 * 1.3.0 (2026-10-19): the statement flags for Accounts — invoices owed at the
 * period end but paid just after it, and credit left unallocated while the
 * customer owes (utils/statementReviewFlags.js for the words).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';
import './ReviewFlagsPanel.css';
import { showAlert } from './Alert';
import {
  STATEMENT_FLAG_LABELS, PAID_AFTER_PERIOD, CREDIT_UNALLOCATED,
  paidAfterPeriodText, creditUnallocatedText
} from '../utils/statementReviewFlags';

const LABELS = {
  till_invoice_discrepancy: 'Invoice does not match the till receipt',
  till_uninvoiced: 'Till transaction with no invoice',
  local_only_invoice: 'Invoice has not reached Xero',
  ...STATEMENT_FLAG_LABELS
};

// Why the push button is greyed on a row that is not an invoice waiting for Xero.
const NO_PUSH_REASON = {
  [PAID_AFTER_PERIOD]: 'Nothing to push to Xero — these invoices are in Xero and were paid just after the period end',
  [CREDIT_UNALLOCATED]: 'Nothing to push to Xero — allocate the credit in Xero itself'
};

const money = (v) => {
  const n = parseFloat(v) || 0;
  return (n < 0 ? '−£' : '£') + Math.abs(n).toFixed(2);
};
const shortDate = (v) => (v ? new Date(v).toLocaleDateString('en-GB') : '—');
const dateTime = (v) => (v ? new Date(v).toLocaleString('en-GB', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
}) : '—');

/**
 * Surfaces data-quality flags for a given context and lets a user mark one
 * reviewed. Renders nothing at all when there is nothing to report, so it can
 * sit permanently in a screen without adding noise.
 *
 * @param {'till'|'statements'} context - which set of checks to run
 * @param {string} [from] - ISO date, inclusive
 * @param {string} [to] - ISO date, inclusive
 * @param {number} [refreshKey] - change to force a re-fetch
 * @param {function} [onCountChange] - called with the outstanding flag count
 */
function ReviewFlagsPanel({ context, from = null, to = null, refreshKey = 0, onCountChange, hide = null }) {
  const [flags, setFlags] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [pushingKey, setPushingKey] = useState(null);
  /* Collapsed on every load, not just the first. This is a standing advisory
     list, not an alert: it is normal for it to hold a few rows, and open by
     default it pushed the day's transactions down the page every single time
     someone opened Till Sales. The header still says how many there are, so
     nothing is hidden — only folded. */
  const [collapsed, setCollapsed] = useState(true);

  const load = useCallback(async () => {
    // The statements check is period-bound and meaningless without one.
    if (context === 'statements' && (!from || !to)) { setFlags(null); return; }
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
      const res = await fetch(`${API_URL}/api/review-flags/${context}?${qs}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setFlags(data);
    } catch (err) {
      console.error('Failed to load review flags:', err);
    } finally {
      setLoading(false);
    }
  }, [context, from, to]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const hidden = new Set(hide || []);
  const group = (key) => (hidden.has(key) ? [] : (flags?.[key] || []));
  const rows = flags
    ? [...group('discrepancies'), ...group('uninvoiced'), ...group('localOnly'),
       ...group('paidAfterPeriod'), ...group('creditUnallocated')]
    : [];

  useEffect(() => {
    if (onCountChange) onCountChange(rows.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length]);

  // Retry the Xero push straight from the flag that reported it — this is
  // where the problem is noticed, so it's where the fix belongs.
  const pushToXero = async (flag) => {
    const key = `${flag.flagType}:${flag.entity_key}`;
    setPushingKey(key);
    try {
      const res = await fetch(`${API_URL}/api/invoices/${flag.invoice_id}/push-to-xero`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Push failed');
      showAlert(`${flag.invoice_no} pushed to Xero.`, 'success');
      await load(); // succeeded, so the flag should disappear on its own
    } catch (err) {
      console.error('Failed to push invoice to Xero:', err);
      // Keep the row visible with the reason rather than silently doing nothing.
      showAlert(`Could not push ${flag.invoice_no} to Xero: ${err.message}`, 'error');
    } finally {
      setPushingKey(null);
    }
  };

  const acknowledge = async (flag) => {
    const key = `${flag.flagType}:${flag.entity_key}`;
    setBusyKey(key);
    try {
      const res = await fetch(`${API_URL}/api/review-flags/acknowledge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          flagType: flag.flagType,
          entityKey: flag.entity_key,
          note: 'Reviewed — no action required'
        })
      });
      if (res.ok) await load();
    } catch (err) {
      console.error('Failed to acknowledge flag:', err);
    } finally {
      setBusyKey(null);
    }
  };

  if (!loading && rows.length === 0) return null;

  return (
    <div className={`review-flags${collapsed ? ' collapsed' : ''}`}>
      <button
        type="button"
        className="rf-head"
        onClick={() => setCollapsed(c => !c)}
        aria-expanded={!collapsed}
      >
        <span className="material-symbols-rounded rf-icon">flag</span>
        <span className="rf-title">
          {rows.length} {rows.length === 1 ? 'item needs' : 'items need'} a look
        </span>
        <span className="rf-sub">
          {context === 'till'
            ? 'Found while processing till sales'
            : 'For this statement period'}
        </span>
        <span className="material-symbols-rounded rf-chev">
          {collapsed ? 'expand_more' : 'expand_less'}
        </span>
      </button>

      {!collapsed && (
        <div className="rf-body">
          {rows.map(flag => {
            const key = `${flag.flagType}:${flag.entity_key}`;
            return (
              <div className="rf-row" key={key}>
                <div className="rf-main">
                  <span className="rf-label">{LABELS[flag.flagType] || flag.flagType}</span>
                  <span className="rf-detail">
                    {flag.flagType === 'till_invoice_discrepancy' && (
                      <>
                        <b>{flag.invoice_no}</b> · {flag.company_name || 'Unknown customer'} ·{' '}
                        {dateTime(flag.transaction_date)} · paid by {flag.tender || 'unknown'} ·
                        {' '}till {money(flag.till_total)} vs invoice {money(flag.invoice_total)}
                        {' '}(<b className="rf-gap">{money(flag.gap)}</b>)
                      </>
                    )}
                    {flag.flagType === 'till_uninvoiced' && (
                      <>
                        {flag.company_name} · {dateTime(flag.transaction_date)} ·{' '}
                        {flag.tender || 'unknown tender'} · <b>{money(flag.total)}</b>
                      </>
                    )}
                    {flag.flagType === 'local_only_invoice' && (
                      <>
                        <b>{flag.invoice_no}</b> · {flag.company_name || 'Unknown customer'} ·{' '}
                        {shortDate(flag.invoice_date)} · <b>{money(flag.total)}</b>
                        {flag.xero_push_error ? ` · ${String(flag.xero_push_error).slice(0, 120)}` : ''}
                      </>
                    )}
                    {(flag.flagType === PAID_AFTER_PERIOD || flag.flagType === CREDIT_UNALLOCATED) && (() => {
                      const t = flag.flagType === PAID_AFTER_PERIOD ? paidAfterPeriodText(flag) : creditUnallocatedText(flag);
                      return (
                        <>
                          <b>{t.customer}</b> · {t.detail}
                          <span className="rf-hint">{t.hint}</span>
                        </>
                      );
                    })()}
                  </span>
                </div>
                <div className="rf-actions">
                  {/* Same two buttons on every row (disable, never hide), so
                      the rows' actions line up whatever kind of flag each is. */}
                  {flag.flagType === 'local_only_invoice' ? (
                    <button
                      type="button"
                      className="btn-icon"
                      title={`Push ${flag.invoice_no} to Xero now`}
                      disabled={pushingKey === key}
                      onClick={() => pushToXero(flag)}
                    >
                      <span className="material-symbols-rounded">cloud_upload</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn-icon btn-disabled"
                      title={NO_PUSH_REASON[flag.flagType] || 'Nothing to push to Xero — this flag is about a till sale, not an invoice waiting for Xero'}
                      disabled
                    >
                      <span className="material-symbols-rounded">cloud_upload</span>
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn-icon rf-ack"
                    title="Mark as reviewed — no action required"
                    disabled={busyKey === key}
                    onClick={() => acknowledge(flag)}
                  >
                    <span className="material-symbols-rounded">check</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ReviewFlagsPanel;
